/**
 * PagesModule — Gestión del Page Object Model (POM).
 *
 * Jerarquía: Sistema → Versión → Pantalla → Objetos de pantalla
 * El POM cuelga de Versión (clonable junto con el resto del árbol),
 * así que este módulo elige primero la versión (por defecto la activa).
 * Layout de 4 columnas similar a SystemsModule.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { systems as systemsApi, versions as versionsApi, pagesApi } from '../../services/api';
import type { TestSystem, Version } from '../../types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ScreenPage {
  id: string;
  version_id: string;
  name: string;
  description?: string;
  url_pattern?: string;
  order_index: number;
}

interface PageObject {
  id: string;
  page_id: string;
  name: string;
  description?: string;
  selector_primary: string;
  selector_fallbacks: string[];
  selector_strategy: string;
  element_type: string;
  order_index: number;
}

// ─── Element type config ──────────────────────────────────────────────────────

const ELEMENT_TYPE_CFG: Record<string, { icon: string; color: string; bg: string }> = {
  button:  { icon: '⊡', color: '#185FA5', bg: '#E6F1FB' },
  input:   { icon: '▭', color: '#0F6E56', bg: '#E1F5EE' },
  link:    { icon: '⇗', color: '#534AB7', bg: '#EEEDFE' },
  select:  { icon: '▾', color: '#854F0B', bg: '#FAEEDA' },
  image:   { icon: '⬚', color: '#5F5E5A', bg: '#F1EFE8' },
  table:   { icon: '⊞', color: '#793C1D', bg: '#FAECE7' },
  element: { icon: '◉', color: '#5F5E5A', bg: '#F1EFE8' },
};

const STRATEGY_COLORS: Record<string, string> = {
  css: '#185FA5', xpath: '#534AB7', text: '#0F6E56',
  testid: '#854F0B', role: '#5F3B00',
};

const VERSION_STATUS_CFG: Record<string, { color: string; bg: string; label: string }> = {
  draft:    { color: '#854F0B', bg: '#FAEEDA', label: 'Borrador' },
  active:   { color: '#0F6E56', bg: '#E1F5EE', label: 'Activa' },
  archived: { color: '#5F5E5A', bg: '#F1EFE8', label: 'Archivada' },
};

// ─── Shared UI ────────────────────────────────────────────────────────────────

const INPUT: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'var(--color-background-secondary)',
  color: 'var(--color-text-primary)', fontSize: 13,
  boxSizing: 'border-box', outline: 'none',
};

function Spinner() {
  return <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 12 }}>Cargando...</div>;
}

function EmptyState({ icon, text, action }: { icon: string; text: string; action?: React.ReactNode }) {
  return (
    <div style={{ padding: '36px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: 'var(--color-text-tertiary)' }}>
      <span style={{ fontSize: 28 }}>{icon}</span>
      <span style={{ fontSize: 12, textAlign: 'center' }}>{text}</span>
      {action}
    </div>
  );
}

function Modal({ title, subtitle, onClose, children }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 520, maxHeight: '85vh', borderRadius: 16, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-secondary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function ColHeader({ label, onNew, disabled }: { label: string; onNew?: () => void; disabled?: boolean }) {
  return (
    <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      {onNew && !disabled && (
        <button onClick={onNew} style={{ border: 'none', background: '#3B8BD4', color: '#fff', borderRadius: 5, padding: '2px 7px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>+ Nuevo</button>
      )}
    </div>
  );
}

function ItemRow({ label, sublabel, selected, onClick, onEdit, onDelete, badge }: {
  label: string; sublabel?: string; selected: boolean;
  onClick: () => void; onEdit: () => void; onDelete: () => void;
  badge?: React.ReactNode;
}) {
  return (
    <div onClick={onClick} style={{ padding: '10px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, border: `1px solid ${selected ? '#3B8BD4' : 'var(--color-border-tertiary)'}`, background: selected ? '#E6F1FB22' : 'var(--color-background-primary)', transition: 'all 0.15s' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
          {sublabel && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{sublabel}</div>}
          {badge && <div style={{ marginTop: 4 }}>{badge}</div>}
        </div>
        <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button onClick={onEdit} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✎</button>
          <button onClick={onDelete} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✕</button>
        </div>
      </div>
    </div>
  );
}

// ─── Page Modal ───────────────────────────────────────────────────────────────

function PageModal({ initial, systemName, onSave, onClose }: {
  initial?: Partial<ScreenPage>; systemName: string;
  onSave: (d: Partial<ScreenPage>) => Promise<void>; onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [url, setUrl] = useState(initial?.url_pattern ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ name, description: desc, url_pattern: url });
    setSaving(false);
    onClose();
  };

  return (
    <Modal title={initial?.id ? 'Editar pantalla' : 'Nueva pantalla'} subtitle={systemName} onClose={onClose}>
      <Field label="Nombre *"><input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="Login, Dashboard, Checkout..." /></Field>
      <Field label="Descripción"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={2} value={desc} onChange={e => setDesc(e.target.value)} /></Field>
      <Field label="URL / Patrón" hint="Ejemplo: /login, /dashboard, /products/*">
        <input style={INPUT} value={url} onChange={e => setUrl(e.target.value)} placeholder="/login" />
      </Field>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
        <button onClick={save} disabled={saving || !name.trim()} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Object Modal ─────────────────────────────────────────────────────────────

function ObjectModal({ initial, pageName, onSave, onClose }: {
  initial?: Partial<PageObject>; pageName: string;
  onSave: (d: Partial<PageObject>) => Promise<void>; onClose: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [desc, setDesc] = useState(initial?.description ?? '');
  const [selector, setSelector] = useState(initial?.selector_primary ?? '');
  const [fallbacks, setFallbacks] = useState((initial?.selector_fallbacks ?? []).join('\n'));
  const [strategy, setStrategy] = useState(initial?.selector_strategy ?? 'css');
  const [elType, setElType] = useState(initial?.element_type ?? 'element');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validate = (n = name, s = selector) => {
    const e: Record<string, string> = {};
    if (!n.trim()) e.name = 'El nombre es requerido';
    if (!s.trim()) e.selector = 'El selector es requerido';
    else if (s.trim().length < 2) e.selector = 'El selector debe tener al menos 2 caracteres';
    return e;
  };

  const touch = (field: string) => setTouched(prev => ({ ...prev, [field]: true }));

  const save = async () => {
    setTouched({ name: true, selector: true });
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSaving(true);
    try {
      await onSave({
        name, description: desc,
        selector_primary: selector,
        selector_fallbacks: fallbacks.split('\n').map(s => s.trim()).filter(Boolean),
        selector_strategy: strategy,
        element_type: elType,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const fieldError = (field: string) =>
    touched[field] && errors[field]
      ? <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>⚠ {errors[field]}</div>
      : null;

  return (
    <Modal title={initial?.id ? 'Editar objeto' : 'Nuevo objeto'} subtitle={pageName} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Nombre *">
          <input
            style={{ ...INPUT, borderColor: touched.name && errors.name ? '#E24B4A' : undefined }}
            value={name}
            onChange={e => { setName(e.target.value); if (touched.name) setErrors(validate(e.target.value, selector)); }}
            onBlur={() => { touch('name'); setErrors(validate()); }}
            placeholder="Campo Email, Botón Entrar..."
          />
          {fieldError('name')}
        </Field>
        </div>
        <Field label="Tipo de elemento">
          <select style={INPUT} value={elType} onChange={e => setElType(e.target.value)}>
            {Object.keys(ELEMENT_TYPE_CFG).map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Estrategia">
          <select style={INPUT} value={strategy} onChange={e => setStrategy(e.target.value)}>
            {['css', 'xpath', 'text', 'testid', 'role'].map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
          </select>
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Selector principal *" hint="El selector que se usará por defecto en las acciones">
            <input
              style={{ ...INPUT, fontFamily: 'monospace', borderColor: touched.selector && errors.selector ? '#E24B4A' : undefined }}
              value={selector}
              onChange={e => { setSelector(e.target.value); if (touched.selector) setErrors(validate(name, e.target.value)); }}
              onBlur={() => { touch('selector'); setErrors(validate()); }}
              placeholder="#email, [name='email'], //input[@type='email']"
            />
            {fieldError('selector')}
          </Field>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Selectores alternativos" hint="Uno por línea — se prueban si el principal falla">
            <textarea style={{ ...INPUT, fontFamily: 'monospace', resize: 'vertical' }} rows={3} value={fallbacks} onChange={e => setFallbacks(e.target.value)} placeholder="input[name='email']&#10;[data-testid='email-field']" />
          </Field>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Descripción"><textarea style={{ ...INPUT, resize: 'vertical' }} rows={2} value={desc} onChange={e => setDesc(e.target.value)} /></Field>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
        <button onClick={save} disabled={saving}
          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Objects Panel ────────────────────────────────────────────────────────────

function ObjectsPanel({ page, onNewObject, objects, loading, onEdit, onDelete, onDetail, copiedId, setCopiedId }: {
  page: ScreenPage; onNewObject: () => void;
  objects: PageObject[]; loading: boolean;
  onEdit: (obj: PageObject) => void; onDelete: (obj: PageObject) => void;
  onDetail: (obj: PageObject) => void; copiedId: string | null; setCopiedId: (id: string | null) => void;
}) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text-primary)' }}>{page.name}</div>
          {page.url_pattern && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontFamily: 'monospace', marginTop: 2 }}>{page.url_pattern}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', alignSelf: 'center' }}>{objects.length} objeto{objects.length !== 1 ? 's' : ''}</span>
          <button onClick={onNewObject} style={{ border: 'none', background: '#3B8BD4', color: '#fff', borderRadius: 6, padding: '5px 12px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>+ Nuevo objeto</button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {loading ? <Spinner />
        : objects.length === 0
          ? <EmptyState icon="◉" text="Sin objetos — agrega elementos de esta pantalla" action={<button onClick={onNewObject} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', fontSize: 12, cursor: 'pointer' }}>+ Nuevo objeto</button>} />
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {objects.map(obj => {
                const tc = ELEMENT_TYPE_CFG[obj.element_type] ?? ELEMENT_TYPE_CFG.element;
                const sc = STRATEGY_COLORS[obj.selector_strategy] ?? '#185FA5';
                return (
                  <div key={obj.id}
                    onClick={() => onDetail(obj)}
                    style={{ padding: '10px 12px', borderRadius: 10, border: '1px solid var(--color-border-tertiary)', background: 'var(--color-background-secondary)', cursor: 'pointer', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#7F77DD'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-tertiary)'}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ padding: '2px 7px', borderRadius: 5, background: tc.bg, color: tc.color, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{tc.icon} {obj.element_type}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4 }}>{obj.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ padding: '1px 5px', borderRadius: 3, background: sc + '22', color: sc, fontSize: 9, fontWeight: 700, textTransform: 'uppercase' }}>{obj.selector_strategy}</span>
                          <code style={{ fontSize: 11, color: 'var(--color-text-primary)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                            {obj.selector_primary}
                          </code>
                        </div>
                        {obj.selector_fallbacks.length > 0 && (
                          <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 3 }}>
                            {obj.selector_fallbacks.length} alternativo{obj.selector_fallbacks.length !== 1 ? 's' : ''}
                          </div>
                        )}
                        {obj.description && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 3 }}>{obj.description}</div>}
                      </div>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => { navigator.clipboard.writeText(obj.selector_primary).then(() => { setCopiedId(obj.id); setTimeout(() => setCopiedId(null), 1500); }); }}
                          title="Copiar selector"
                          style={{ border: '1px solid var(--color-border-secondary)', background: copiedId === obj.id ? '#E1F5EE' : 'var(--color-background-primary)', cursor: 'pointer', fontSize: 10, color: copiedId === obj.id ? '#0F6E56' : 'var(--color-text-tertiary)', padding: '2px 6px', borderRadius: 4 }}>
                          {copiedId === obj.id ? '✓' : '📋'}
                        </button>
                        <button onClick={() => onEdit(obj)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✎</button>
                        <button onClick={() => onDelete(obj)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 11, color: 'var(--color-text-tertiary)', padding: '1px 4px' }}>✕</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}

// ─── Main PagesModule ─────────────────────────────────────────────────────────

// ─── Object Detail Modal ─────────────────────────────────────────────────────

function ObjectDetailModal({ obj, pageName, onClose, onEdit }: {
  obj: PageObject; pageName: string;
  onClose: () => void; onEdit: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const tc = ELEMENT_TYPE_CFG[obj.element_type] ?? ELEMENT_TYPE_CFG.element;
  const sc = STRATEGY_COLORS[obj.selector_strategy] ?? '#185FA5';

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const allSelectors = [
    { label: obj.selector_strategy.toUpperCase(), value: obj.selector_primary, key: 'primary' },
    ...obj.selector_fallbacks.map((s, i) => ({ label: `Alternativo ${i + 1}`, value: s, key: `alt-${i}` })),
  ];

  return (
    <Modal title={obj.name} subtitle={pageName} onClose={onClose}>
      {/* Type + strategy badges */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <span style={{ padding: '3px 10px', borderRadius: 6, background: tc.bg, color: tc.color, fontSize: 11, fontWeight: 700 }}>
          {tc.icon} {obj.element_type}
        </span>
        <span style={{ padding: '3px 10px', borderRadius: 6, background: sc + '22', color: sc, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
          {obj.selector_strategy}
        </span>
      </div>

      {/* Description */}
      {obj.description && (
        <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--color-background-secondary)', fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
          {obj.description}
        </div>
      )}

      {/* Selectors */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          Selectores
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {allSelectors.map(s => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', minWidth: 70 }}>{s.label}</span>
              <code style={{ flex: 1, fontSize: 12, fontFamily: 'monospace', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>{s.value}</code>
              <button
                onClick={() => copy(s.value, s.key)}
                style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid var(--color-border-secondary)', background: copied === s.key ? '#E1F5EE' : 'var(--color-background-primary)', color: copied === s.key ? '#0F6E56' : 'var(--color-text-tertiary)', fontSize: 10, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                {copied === s.key ? '✓ Copiado' : '📋 Copiar'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Cerrar</button>
        <button onClick={onEdit} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✎ Editar</button>
      </div>
    </Modal>
  );
}

export function PagesModule() {
  const [systems, setSystems] = useState<TestSystem[]>([]);
  const [selectedSystem, setSelectedSystem] = useState<TestSystem | null>(null);
  const [loadingSystems, setLoadingSystems] = useState(true);

  const [versions, setVersions] = useState<Version[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const [pages, setPages] = useState<ScreenPage[]>([]);
  const [selectedPage, setSelectedPage] = useState<ScreenPage | null>(null);
  const [loadingPages, setLoadingPages] = useState(false);
  const [pageModal, setPageModal] = useState<'create' | ScreenPage | null>(null);

  const [objects, setObjects] = useState<PageObject[]>([]);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [objectModal, setObjectModal] = useState<'create' | PageObject | null>(null);
  const [detailObject, setDetailObject] = useState<PageObject | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [toast, setToast] = useState<string | null>(null);

  const notify = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load systems
  useEffect(() => {
    setLoadingSystems(true);
    systemsApi.list().then(s => { setSystems(s); if (s.length) setSelectedSystem(s[0]); })
      .finally(() => setLoadingSystems(false));
  }, []);

  // Load versions when system changes — preselect la versión activa
  useEffect(() => {
    if (!selectedSystem) { setVersions([]); setSelectedVersion(null); return; }
    setLoadingVersions(true);
    versionsApi.list(selectedSystem.id)
      .then(v => { setVersions(v); setSelectedVersion(v.find(x => x.status === 'active') ?? v[0] ?? null); })
      .finally(() => setLoadingVersions(false));
  }, [selectedSystem?.id]);

  // Load pages when version changes
  useEffect(() => {
    if (!selectedVersion) { setPages([]); setSelectedPage(null); return; }
    setLoadingPages(true);
    pagesApi.listPages(selectedVersion.id)
      .then(p => { setPages(p as unknown as ScreenPage[]); setSelectedPage(null); })
      .finally(() => setLoadingPages(false));
  }, [selectedVersion?.id]);

  // Load objects when page changes
  useEffect(() => {
    if (!selectedPage) { setObjects([]); return; }
    setLoadingObjects(true);
    pagesApi.listObjects(selectedPage.id)
      .then(o => setObjects(o as unknown as PageObject[]))
      .finally(() => setLoadingObjects(false));
  }, [selectedPage?.id]);

  // Pages CRUD
  const handleCreatePage = async (d: Partial<ScreenPage>) => {
    if (!selectedVersion) return;
    const p = await pagesApi.createPage(selectedVersion.id, d) as unknown as ScreenPage;
    setPages(prev => [...prev, p]);
    setSelectedPage(p);
    notify('Pantalla creada ✓');
  };
  const handleEditPage = async (d: Partial<ScreenPage>) => {
    if (typeof pageModal !== 'object' || !pageModal) return;
    const p = await pagesApi.updatePage(pageModal.id, d) as unknown as ScreenPage;
    setPages(prev => prev.map(x => x.id === p.id ? p : x));
    if (selectedPage?.id === p.id) setSelectedPage(p);
    notify('Pantalla actualizada ✓');
  };
  const handleDeletePage = async (page: ScreenPage) => {
    if (!window.confirm(`¿Eliminar pantalla "${page.name}"?`)) return;
    await pagesApi.deletePage(page.id);
    setPages(prev => prev.filter(x => x.id !== page.id));
    if (selectedPage?.id === page.id) { setSelectedPage(null); setObjects([]); }
    notify('Pantalla eliminada ✓');
  };

  // Objects CRUD
  const handleCreateObject = async (d: Partial<PageObject>) => {
    if (!selectedPage) return;
    const o = await pagesApi.createObject(selectedPage.id, { ...d, order_index: objects.length }) as unknown as PageObject;
    setObjects(prev => [...prev, o]);
    notify('Objeto creado ✓');
  };
  const handleEditObject = async (d: Partial<PageObject>) => {
    if (typeof objectModal !== 'object' || !objectModal) return;
    const o = await pagesApi.updateObject(objectModal.id, d) as unknown as PageObject;
    setObjects(prev => prev.map(x => x.id === o.id ? o : x));
    notify('Objeto actualizado ✓');
  };
  const handleDeleteObject = async (obj: PageObject) => {
    if (!window.confirm(`¿Eliminar objeto "${obj.name}"?`)) return;
    await pagesApi.deleteObject(obj.id);
    setObjects(prev => prev.filter(x => x.id !== obj.id));
    notify('Objeto eliminado ✓');
  };

  const colStyle = (width: number): React.CSSProperties => ({
    width, flexShrink: 0, display: 'flex', flexDirection: 'column',
    borderRight: '1px solid var(--color-border-tertiary)',
  });

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden', background: 'var(--color-background-primary)' }}>

      {/* COL 1: Systems */}
      <div style={colStyle(200)}>
        <ColHeader label="Sistemas" />
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {loadingSystems ? <Spinner />
          : systems.length === 0 ? <EmptyState icon="⬡" text="Sin sistemas definidos" />
          : systems.map(sys => (
            <div key={sys.id} onClick={() => setSelectedSystem(sys)}
              style={{ padding: '10px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, border: `1px solid ${selectedSystem?.id === sys.id ? '#3B8BD4' : 'var(--color-border-tertiary)'}`, background: selectedSystem?.id === sys.id ? '#E6F1FB22' : 'var(--color-background-primary)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)' }}>⬡ {sys.name}</div>
              {sys.base_url && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sys.base_url}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* COL 2: Versions */}
      <div style={colStyle(170)}>
        <ColHeader label="Versiones" />
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {!selectedSystem ? <EmptyState icon="←" text="Selecciona un sistema" />
          : loadingVersions ? <Spinner />
          : versions.length === 0 ? <EmptyState icon="⧉" text="Sin versiones" />
          : versions.map(ver => {
            const vc = VERSION_STATUS_CFG[ver.status] ?? VERSION_STATUS_CFG.draft;
            return (
              <div key={ver.id} onClick={() => setSelectedVersion(ver)}
                style={{ padding: '10px 10px', borderRadius: 8, cursor: 'pointer', marginBottom: 4, border: `1px solid ${selectedVersion?.id === ver.id ? '#3B8BD4' : 'var(--color-border-tertiary)'}`, background: selectedVersion?.id === ver.id ? '#E6F1FB22' : 'var(--color-background-primary)' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)' }}>⧉ {ver.name}</div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ padding: '1px 7px', borderRadius: 5, background: vc.bg, color: vc.color, fontSize: 10, fontWeight: 700 }}>{vc.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* COL 3: Pages */}
      <div style={colStyle(210)}>
        <ColHeader label="Pantallas" onNew={() => setPageModal('create')} disabled={!selectedVersion} />
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {!selectedVersion ? <EmptyState icon="←" text="Selecciona una versión" />
          : loadingPages ? <Spinner />
          : pages.length === 0
            ? <EmptyState icon="◉" text="Sin pantallas" action={<button onClick={() => setPageModal('create')} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', fontSize: 12, cursor: 'pointer' }}>Crear pantalla</button>} />
          : pages.map(page => (
            <ItemRow key={page.id} label={page.name} sublabel={page.url_pattern ?? undefined}
              selected={selectedPage?.id === page.id}
              onClick={() => setSelectedPage(page)}
              onEdit={() => setPageModal(page)}
              onDelete={() => handleDeletePage(page)} />
          ))}
        </div>
      </div>

      {/* COL 4: Objects */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {!selectedPage
          ? <EmptyState icon="←" text="Selecciona una pantalla para ver sus objetos" />
          : <ObjectsPanel
              page={selectedPage}
              onNewObject={() => setObjectModal('create')}
              objects={objects}
              loading={loadingObjects}
              onEdit={obj => setObjectModal(obj)}
              onDelete={handleDeleteObject}
              onDetail={obj => setDetailObject(obj)}
              copiedId={copiedId}
              setCopiedId={setCopiedId}
            />
        }
      </div>

      {/* Modals */}
      {pageModal !== null && (
        <PageModal
          initial={pageModal === 'create' ? undefined : pageModal}
          systemName={[selectedSystem?.name, selectedVersion?.name].filter(Boolean).join(' · ')}
          onSave={pageModal === 'create' ? handleCreatePage : handleEditPage}
          onClose={() => setPageModal(null)}
        />
      )}
      {detailObject && selectedPage && (
        <ObjectDetailModal
          obj={detailObject}
          pageName={selectedPage.name}
          onClose={() => setDetailObject(null)}
          onEdit={() => { setObjectModal(detailObject); setDetailObject(null); }}
        />
      )}
      {objectModal !== null && selectedPage && (
        <ObjectModal
          initial={objectModal === 'create' ? undefined : objectModal}
          pageName={selectedPage.name}
          onSave={objectModal === 'create' ? handleCreateObject : handleEditObject}
          onClose={() => setObjectModal(null)}
        />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#1a1a18', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 13, fontWeight: 500, zIndex: 500 }}>
          {toast}
        </div>
      )}
    </div>
  );
}